/**
 * 探测线上当前部署的版本，与本次页面构建的版本（编译期常量 __APP_VERSION__）比对。
 *
 * 数据来源是构建产物 /version.json（见 build/script/writeVersionFile.ts），
 * nginx 侧有 `location = /version.json` 精确匹配保证不缓存、缺失时返回真 404。
 *
 * 设计原则：**一切异常都归为 'unknown'，即"不刷新"**。
 * 宁可漏刷一次（下一轮 5 分钟后会重试），也不能因为一次解析失败就 reload 用户页面。
 */

export type VersionCheckResult = 'same' | 'changed' | 'unknown';

export interface VersionCheckOutcome {
  result: VersionCheckResult;
  /** 远端版本号，仅 result === 'changed' 时有值 */
  version?: string;
}

/**
 * 版本标识的合法形态：CI 注入的是 GitLab 的 commit 短 SHA（前 8 位十六进制）。
 * 校验它是为了挡住"中间层把 HTML 或异常内容返回给 version.json"这类情况——
 * 否则前端会拿到一个乱七八糟的字符串、判定为新版本，陷入刷新循环。
 */
const VERSION_SHAPE = /^[0-9a-f]{7,40}$/i;

export function isValidVersionShape(version: string): boolean {
  return VERSION_SHAPE.test(version);
}

export async function checkRemoteVersion(timeoutMs: number): Promise<VersionCheckOutcome> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // 用 fetch 而不是项目的 axios 实例：自动刷新必须完全静默，
    // 走 axios 会带上 token、全局 loading、失败 message 弹窗与 axiosRetry，污染 UI。
    // - cache: 'no-store' 穿透受控范围之外的中间缓存（prod 走 APISIX，缓存插件不在本仓库）
    // - ?_= 时间戳 绕过浏览器对同一 URL 的启发式复用（Chrome 短时间內可能不发请求）
    const res = await fetch(`${import.meta.env.BASE_URL}version.json?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (!res.ok) return { result: 'unknown' };

    const data = await res.json();
    const remote = String(data?.version ?? '').trim();
    if (!remote) return { result: 'unknown' };
    if (remote === __APP_VERSION__) return { result: 'same' };
    if (!isValidVersionShape(remote)) return { result: 'unknown' };

    return { result: 'changed', version: remote };
  } catch {
    // 网络失败 / 超时 / JSON 解析失败 / 404 回落成 HTML —— 全部按"不刷新"处理
    return { result: 'unknown' };
  } finally {
    clearTimeout(timer);
  }
}
